'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';

import Select from '@app/pro/components/Widgets/Select.react';
import allNutrients from '@app/tables/nutrients';
import { fetchCapture, imageUrl, searchFoods, serveThreshold } from '@utils/Monash';

import './MonashBrowserModal.scss';

const PAGE_SIZE = 60;

// The six FODMAPs the entry tool rows cover, in tool order
const DOT_NUTRNOS = ['FRC', 'LAC', 'MAN', 'SOR', 'OLG', 'OLF'];

const LIGHT_LABELS = { 1: 'green', 2: 'amber', 3: 'red' };

// Full-capture browser over the Monash capture (plan §5.7): search with
// stemming, filter by category / overall light / certified, pick a food to
// prefill the FODMAP entry tool. Stacks on top of FodmapEntryModal.
export default class MonashBrowserModal extends Component {
    static propTypes = {
        onPick: PropTypes.func.isRequired,
        closeModal: PropTypes.func.isRequired,
    };

    state = {
        capture: null,
        query: '',
        categoryId: '',
        lights: { 1: true, 2: true, 3: true },
        certifiedOnly: false,
        shown: PAGE_SIZE,
    };

    componentDidMount = () => {
        fetchCapture().then((capture) => {
            this.manufacturersById = Object.fromEntries(
                capture.manufacturers.map((manufacturer) => [manufacturer.uuid, manufacturer])
            );

            this.setState({ capture });
        });
    };

    onChangeQuery = (ev) => {
        this.setState({ query: ev.target.value, shown: PAGE_SIZE });
    };

    onChangeCategory = (categoryId) => {
        this.setState({ categoryId, shown: PAGE_SIZE });
    };

    toggleLight = (light) => {
        const lights = { ...this.state.lights, [light]: !this.state.lights[light] };

        this.setState({ lights, shown: PAGE_SIZE });
    };

    toggleCertified = () => {
        this.setState({ certifiedOnly: !this.state.certifiedOnly, shown: PAGE_SIZE });
    };

    isFiltered = () => {
        const { categoryId, lights, certifiedOnly } = this.state;

        return Boolean(categoryId) || certifiedOnly || !(lights[1] && lights[2] && lights[3]);
    };

    resetFilters = () => {
        this.setState({
            categoryId: '',
            lights: { 1: true, 2: true, 3: true },
            certifiedOnly: false,
            shown: PAGE_SIZE,
        });
    };

    // Search runs on the stable capture.foods reference so the token index
    // memoizes; filters compose on the ranked result
    results = () => {
        const { capture, query, categoryId, lights, certifiedOnly } = this.state;

        const foods = query.trim() ? searchFoods(capture.foods, query, Infinity) : capture.foods;

        return foods.filter(
            (food) =>
                lights[food.overall] &&
                (!categoryId || food.category_ids.includes(categoryId)) &&
                (!certifiedOnly || food.certified === 't')
        );
    };

    renderFodmapDots = (food) => (
        <span className="fodmap-dots">
            {DOT_NUTRNOS.map((nutrNo) => {
                const threshold = serveThreshold(food, nutrNo);

                return (
                    <i
                        key={nutrNo}
                        data-level={threshold ? threshold.level : 'none'}
                        title={
                            threshold
                                ? `${allNutrients[nutrNo].NutrDesc}: ` +
                                  `${LIGHT_LABELS[threshold.level + 1]} at ${threshold.size}g`
                                : `${allNutrients[nutrNo].NutrDesc}: no data`
                        }
                    />
                );
            })}
        </span>
    );

    renderRow = (food) => {
        const manufacturer = food.certified_uuid ? this.manufacturersById[food.certified_uuid] : null;

        return (
            <button key={food.food_id} className="monash-row" onClick={() => this.props.onPick(food)}>
                {food.images?.length ? <img src={imageUrl(food.images[0])} alt="" /> : <span className="no-img" />}

                <span className="row-name p3">
                    {food.name}
                    {manufacturer ? <em className="brand-chip">{manufacturer.name}</em> : null}
                </span>

                {this.renderFodmapDots(food)}
                <i className="overall-light" data-overall={food.overall} />
            </button>
        );
    };

    // The chrome renders whether or not the capture has arrived, and the
    // results box is fixed-height, so the modal never resizes — not while
    // loading, and not when a search narrows the list
    renderBrowser = () => {
        const { capture, query, categoryId, lights, certifiedOnly, shown } = this.state;

        const results = capture ? this.results() : [];

        return (
            <>
                <div className="browser-filters">
                    <span className="el-unlabeled-input">
                        <input
                            type="text"
                            placeholder="search Monash foods…"
                            value={query}
                            onChange={this.onChangeQuery}
                            autoFocus
                        />
                    </span>

                    <Select
                        defaultClassName="el-select-container"
                        value={categoryId}
                        options={[
                            { value: '', label: 'All categories' },
                            ...(capture ? capture.categories : []).map((category) => ({
                                value: category.id,
                                label: category.name,
                            })),
                        ]}
                        onChange={this.onChangeCategory}
                    />

                </div>

                <div className="browser-chips">
                    {[1, 2, 3].map((light) => (
                        <button
                            key={light}
                            className="filter-chip light-chip"
                            data-light={light}
                            data-on={lights[light]}
                            onClick={() => this.toggleLight(light)}
                        >
                            {LIGHT_LABELS[light]}
                        </button>
                    ))}

                    <button className="filter-chip" data-on={certifiedOnly} onClick={this.toggleCertified}>
                        certified
                    </button>

                    {this.isFiltered() ? (
                        <button className="el-text-btn reset-filters" onClick={this.resetFilters}>
                            reset
                        </button>
                    ) : null}

                    <span className="result-count p3">
                        {capture ? `${results.length} ${results.length === 1 ? 'food' : 'foods'}` : null}
                    </span>
                </div>

                <div className="browser-results">
                    {capture ? (
                        <>
                            {results.slice(0, shown).map(this.renderRow)}

                            {results.length > shown ? (
                                <button
                                    className="el-text-btn show-more"
                                    onClick={() => this.setState({ shown: shown + PAGE_SIZE })}
                                >
                                    show {Math.min(PAGE_SIZE, results.length - shown)} more
                                </button>
                            ) : null}
                        </>
                    ) : (
                        <p className="loading-note p3">Loading Monash foods…</p>
                    )}
                </div>
            </>
        );
    };

    render = () => {
        const { closeModal } = this.props;

        return (
            <Modal
                isOpen={true}
                onRequestClose={closeModal}
                closeModal={closeModal}
                contentLabel="Monash Food Browser"
                className="el-modal el-modal2"
                overlayClassName="el-modal-overlay"
                closeTimeoutMS={250}
            >
                <div className="el-modal-container el-modal2-container monash-browser-modal-container">
                    <header>
                        <h2>Browse Monash Foods</h2>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container el-fonts">
                        {this.renderBrowser()}
                    </div>

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={closeModal}>
                            close
                        </button>
                    </footer>
                </div>
            </Modal>
        );
    };
}
